import { Rarity } from "@highmarch/domain";
import { PaintedIcon, paintedIconHtml, paintedIconUrl } from "./featureIcons";
import { RARITY_LABEL } from "./gearUi";

/**
 * Gear v2's forge goods as the UI draws them (docs/GEAR_V2.md): Forge Essence (raises an item's level) and the Fusion
 * Sigils (one stands in for an item of its rarity in a fusion). Painted icons from the premium kit
 * (tools/art/icons3d/forge.py): a stoppered flask of molten forge-fire, and a medallion per rarity with three
 * interlocked rings. Markup and words only; the rules are the domain's.
 */

/** The rarities a sigil exists for (a Mythic never fuses). */
export const SIGIL_RARITIES: readonly Rarity[] = [Rarity.Common, Rarity.Uncommon, Rarity.Rare, Rarity.Epic, Rarity.Legendary];

const SIGIL_ICON: Readonly<Record<number, PaintedIcon>> = {
  [Rarity.Common]: "sigilCommon", [Rarity.Uncommon]: "sigilUncommon", [Rarity.Rare]: "sigilRare", [Rarity.Epic]: "sigilEpic", [Rarity.Legendary]: "sigilLegendary",
};

/** The sigil's painted icon name (a Mythic falls back to the Legendary medallion: none is ever paid). */
export function sigilIcon(r: Rarity): PaintedIcon { return SIGIL_ICON[r] ?? "sigilLegendary"; }

/** Forge Essence as an `<img>` (`cls` adds classes). */
export function essenceIconHtml(size = 18, cls = ""): string { return paintedIconHtml("essence", size, `forge ess${cls ? ` ${cls}` : ""}`); }
export function essenceIconUrl(): string { return paintedIconUrl("essence"); }

/** A Fusion Sigil of `r` as an `<img>`. */
export function sigilIconHtml(r: Rarity, size = 18, cls = ""): string { return paintedIconHtml(sigilIcon(r), size, `forge sig r${r}${cls ? ` ${cls}` : ""}`); }
export function sigilIconUrl(r: Rarity): string { return paintedIconUrl(sigilIcon(r)); }

/** "1 Rare sigil", "3 Epic sigils". */
export function sigilWords(r: Rarity, n: number): string { return `${n} ${RARITY_LABEL[r]} sigil${n === 1 ? "" : "s"}`; }

/** "120 essence". */
export function essenceWords(n: number): string { return `${n.toLocaleString("en-US")} essence`; }

/** Sigils held, total. */
export function sigilTotal(sigils: readonly number[]): number { return SIGIL_RARITIES.reduce((n, r) => n + (sigils[r] ?? 0), 0); }

/** The rarest sigil held (its medallion stands for the stack in a counter), or null for none. */
export function topSigil(sigils: readonly number[]): Rarity | null {
  for (let i = SIGIL_RARITIES.length - 1; i >= 0; i--) if ((sigils[SIGIL_RARITIES[i]] ?? 0) > 0) return SIGIL_RARITIES[i];
  return null;
}
